import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { type Err } from '../CreateGroup/useCreateCampaignGroup'

import { ActionModal, FormField, Modal, TextInput } from '@percent/lemonade'
import { useEditCampaignGroup } from './useEditCampaignGroup'

export type EditGroupModalProps = Readonly<{
  isOpen: boolean
  handleClose: () => void
  group: { id: string; name: string }
  onSuccess: () => void
  onError: (error?: Err) => void
}>

export function EditGroupModal({ isOpen, handleClose, group, onSuccess, onError }: EditGroupModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(group.name)

  useEffect(() => {
    if (isOpen) {
      setName(group.name)
    }
  }, [isOpen, group.name])

  const { isLoading, editGroup } = useEditCampaignGroup({
    onEdited: () => {
      onSuccess()
    },
    onError: (error: Err) => {
      onError(error)
    }
  })

  const onClose = () => {
    setName(group.name)
    handleClose()
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ActionModal
        title={t('typography.editGroupTitle')}
        primaryButtonText={t('button.save')}
        secondaryButtonText={t('button.cancel')}
        handleClose={onClose}
        handleSubmit={() => editGroup(group.id, name)}
        loading={isLoading}
        disabled={!name.trim() || name.trim() === group.name.trim()}
      >
        <FormField label={t('typography.groupNameLabel')} necessity="required">
          <TextInput
            name="groupName"
            value={name}
            placeholder={t('typography.groupNamePlaceholder')}
            onChange={e => setName(e.target.value)}
            maxLength={120}
            data-testid="edit-group-name-input"
          />
        </FormField>
      </ActionModal>
    </Modal>
  )
}
